import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, tokenStore } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Mountain, Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';

function destFor(role) {
  if (role === 'admin') return '/admin';
  if (role === 'stakeholder') return '/area';
  if (role === 'customer') return '/cliente';
  return '/';
}

// Pagina PUBBLICA: completamento 2FA dopo un accesso Google (OAuth) quando l'utente
// ha la verifica in due passaggi attiva. Il ``challenge`` è un id opaco (NON un token).
export default function MfaChallenge() {
  const [params] = useSearchParams();
  const challenge = params.get('challenge') || '';
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/auth/mfa/complete', { challenge, code: code.trim() });
      if (data.access_token) {
        tokenStore.setAudience(data.user?.role === 'customer' ? 'customer' : 'staff');
        tokenStore.set(data.access_token, data.refresh_token);
      }
      window.location.assign(destFor(data.user?.role));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'MFA_INVALID') setError('Codice non valido. Riprova.');
      else setError(detail || 'Verifica non riuscita. Riprova ad accedere.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Mountain className="h-5 w-5" /></div>
          <div><p className="font-display text-lg font-bold">Sila Experiences</p><p className="text-xs text-muted-foreground">Verifica in due passaggi</p></div>
        </div>
        {!challenge ? (
          <div data-testid="mfa-challenge-missing">
            <h1 className="font-display text-2xl font-bold tracking-tight">Sessione non valida</h1>
            <p className="mt-2 text-sm text-muted-foreground">Riprova ad accedere.</p>
            <p className="mt-6 text-sm"><Link to="/login" className="inline-flex items-center font-medium text-primary hover:underline"><ArrowLeft className="mr-1.5 h-4 w-4" />Torna all'accesso</Link></p>
          </div>
        ) : (
          <>
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent"><ShieldCheck className="h-6 w-6 text-primary" /></div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Conferma la tua identità</h1>
            <p className="mt-1 text-sm text-muted-foreground">Inserisci il codice a 6 cifre dalla tua app Authenticator (o un codice di recupero) per completare l'accesso.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="mfa-challenge-form">
              <div className="space-y-2"><Label htmlFor="code">Codice</Label><Input id="code" inputMode="numeric" autoFocus required value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="mfa-challenge-input" /></div>
              {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="mfa-challenge-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading} data-testid="mfa-challenge-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Verifica e accedi</Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
